import React, { useState, useMemo, useEffect, useRef } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { z } from 'zod';
import { Header } from '../components/layout/Header';
import { BottomNav } from '../components/layout/BottomNav';
import { PageLayout } from '../components/layout/PageLayout';
import { LoadingLogo } from '../components/common/LoadingLogo';
import { useUserStore } from '../store/useUserStore';
import { useAuth } from '../hooks/useAuth';
import {
  useGetProjects,
  useCreateProject,
  mapApiProjectToProject,
  PROJECT_TYPE_LABEL_TO_API,
  PROJECT_STATUS_LABEL_TO_API,
} from '../api/projects';
import { useGetWorkspaceProjects, useGetWorkspaces, useUpdateWorkspace } from '../api/workspaces';
import { useWorkspaceStore } from '../store/useWorkspaceStore';
import { WorkspaceModal } from '../components/common/WorkspaceModal';
import { CollaboratorsAccessCard } from '../components/projects/CollaboratorsAccessCard';
import { CoverCropModal } from '../components/common/CoverCropModal';
import { createCroppedImageFile, convertHeicToJpeg, type PixelCropArea } from '../utils/imageCrop';
import { uploadFilesToCloudinary } from '../utils/cloudinary';
import { toast } from '../utils/toast';
import {
  FiPlus,
  FiLock,
  FiFolder,
  FiCalendar,
  FiArrowUpRight,
  FiMap,
  FiTv,
  FiMusic,
  FiX,
  FiUsers,
  FiCheckSquare,
  FiDollarSign,
  FiEdit2,
  FiCheck,
  FiUpload,
  FiCrop,
} from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';

const PROJECT_TABS = ['all', 'archived', 'mine', 'shared'] as const;
export type ProjectTab = (typeof PROJECT_TABS)[number];

// `tab` is optional in the URL (defaults to 'all') so existing `<Link to="/projects">`
// call sites elsewhere in the app don't need to pass a search param.
// `workspace` scopes the page to a single workspace's projects (set when a workspace is
// picked from the Workspace modal); omitting it keeps the existing "all my projects" view.
const searchSchema = z.object({
  tab: z.enum(PROJECT_TABS).optional(),
  workspace: z.string().optional(),
});

export const Route = createFileRoute('/projects/')({
  validateSearch: (search: Record<string, unknown>) => searchSchema.parse(search),
  component: ProjectsWorkspacePage,
});

function ProjectsWorkspacePage() {
  const { user } = useUserStore();
  const { isAuthenticated } = useAuth();
  const search = Route.useSearch();
  const tab: ProjectTab = search.tab ?? 'all';
  const workspaceUuid = search.workspace;
  const navigate = useNavigate({ from: Route.fullPath });
  const clearActiveWorkspace = useWorkspaceStore((s) => s.clearActiveWorkspace);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate({ to: '/sign-in', search: { redirect: '/projects' } });
    }
  }, [isAuthenticated, navigate]);

  const [workspaceModalOpen, setWorkspaceModalOpen] = useState(
    isAuthenticated && !workspaceUuid && !useWorkspaceStore.getState().activeWorkspaceUuid,
  );

  useEffect(() => {
    if (!isAuthenticated) return;
    if (!workspaceUuid) {
      const activeUuid = useWorkspaceStore.getState().activeWorkspaceUuid;
      if (activeUuid) {
        navigate({ search: { ...search, workspace: activeUuid } });
      } else {
        setWorkspaceModalOpen(true);
      }
    } else {
      setWorkspaceModalOpen(false);
    }
  }, [workspaceUuid, navigate, search, isAuthenticated]);

  const setTab = (nextTab: ProjectTab) => navigate({ search: { ...search, tab: nextTab } });

  const clearWorkspaceScope = () => {
    clearActiveWorkspace();
    navigate({ search: { tab } });
  };

  // Workspace name for the scoped-view chip — the list endpoint is the only
  // place the API surfaces a workspace's display name.
  const { data: workspacesData } = useGetWorkspaces();
  const activeWorkspace = workspacesData?.results.find((w) => w.uuid === workspaceUuid);
  const isWorkspaceOwner = activeWorkspace?.role === 'owner';
  // Someone else's workspace (shared with the current user, not owned by them). "Shared
  // with me" doesn't apply there, but "mine" and "archived" still do — an editor can have
  // their own (archived) projects inside another user's workspace and needs to reach them.
  const isForeignWorkspace = !!workspaceUuid && !isWorkspaceOwner;

  const [isEditingWorkspaceName, setIsEditingWorkspaceName] = useState(false);
  const [workspaceNameDraft, setWorkspaceNameDraft] = useState('');
  const { mutate: updateWorkspaceName, isPending: isSavingWorkspaceName } = useUpdateWorkspace();

  const startEditingWorkspaceName = () => {
    setWorkspaceNameDraft(activeWorkspace?.name ?? '');
    setIsEditingWorkspaceName(true);
  };

  const saveWorkspaceName = () => {
    const trimmed = workspaceNameDraft.trim();
    if (!trimmed || !workspaceUuid) {
      setIsEditingWorkspaceName(false);
      return;
    }
    updateWorkspaceName(
      { workspaceUuid, name: trimmed },
      { onSuccess: () => setIsEditingWorkspaceName(false) },
    );
  };

  // Shared projects count from global projects endpoint — always available & non-403
  const { data: sharedCountData } = useGetProjects({ tab: 'shared', page_size: 1 });
  const sharedCount = sharedCountData?.tab_counts?.shared ?? 0;

  // Main list queries:
  // 1. When clicking/viewing the shared tab, fetch shared projects via useGetProjects
  const { data: sharedProjectsData, isLoading: isSharedProjectsLoading } = useGetProjects(
    { tab: 'shared', page_size: 100 },
    { enabled: tab === 'shared' },
  );

  // 2. Regular global projects query
  const { data: allProjectsData, isLoading: isAllProjectsLoading } = useGetProjects(
    { tab, page_size: 100 },
    { enabled: !workspaceUuid && tab !== 'shared' },
  );

  // 3. Workspace projects query (shared tab uses global query — workspace endpoint doesn't
  //    support tab=shared for non-guests)
  const { data: workspaceProjectsData, isLoading: isWorkspaceProjectsLoading } =
    useGetWorkspaceProjects(
      workspaceUuid,
      { tab, page_size: 100 },
      {
        enabled:
          !!workspaceUuid &&
          tab !== 'shared' &&
          (!isForeignWorkspace || tab === 'all' || tab === 'mine' || tab === 'archived'),
      },
    );

  const projectsData =
    tab === 'shared' ? sharedProjectsData : workspaceUuid ? workspaceProjectsData : allProjectsData;

  const isLoading =
    tab === 'shared'
      ? isSharedProjectsLoading
      : workspaceUuid
        ? isWorkspaceProjectsLoading
        : isAllProjectsLoading;

  // Lightweight count-only queries for all tab badges
  const { data: allCountData } = useGetProjects(
    { tab: 'all', page_size: 1 },
    { enabled: !workspaceUuid },
  );
  const { data: mineCountData } = useGetProjects(
    { tab: 'mine', page_size: 1 },
    { enabled: !workspaceUuid },
  );
  const { data: workspaceAllCountData } = useGetWorkspaceProjects(workspaceUuid, {
    tab: 'all',
    page_size: 1,
  });
  const { data: workspaceMineCountData } = useGetWorkspaceProjects(
    workspaceUuid,
    { tab: 'mine', page_size: 1 },
    { enabled: !!workspaceUuid },
  );
  // Only needed to decide whether an Editor/Manager should see the Archived tab in someone
  // else's workspace (owners always see it, regardless of count).
  const { data: workspaceArchivedCountData } = useGetWorkspaceProjects(
    workspaceUuid,
    { tab: 'archived', page_size: 1 },
    { enabled: !!workspaceUuid && isForeignWorkspace },
  );

  const { mutate: createProject } = useCreateProject();

  const projects = useMemo(() => {
    const raw = projectsData?.results ?? [];
    const mapped = raw.map((p) => mapApiProjectToProject(p, user?.id));
    // Shared tab results come pre-filtered from the API — no client-side isShared check needed.
    if (tab === 'shared') {
      return mapped;
    }
    return mapped;
  }, [projectsData, user?.id, tab]);

  const counts = useMemo(
    () =>
      workspaceUuid
        ? {
            all: workspaceAllCountData?.tab_counts?.all ?? 0,
            myProjects: workspaceMineCountData?.tab_counts?.mine ?? 0,
            sharedWithMe: sharedCount,
            archived: workspaceArchivedCountData?.tab_counts?.archived ?? 0,
          }
        : {
            all: allCountData?.tab_counts?.all ?? 0,
            myProjects: mineCountData?.tab_counts?.mine ?? 0,
            sharedWithMe: sharedCount,
            archived: 0,
          },
    [
      workspaceUuid,
      allCountData,
      mineCountData,
      workspaceAllCountData,
      workspaceMineCountData,
      workspaceArchivedCountData,
      sharedCount,
    ],
  );

  const showSharedTab = counts.sharedWithMe > 0;

  const [isModalOpen, setIsModalOpen] = useState(false);

  // Manage Access Modal States
  const [isAccessModalOpen, setIsAccessModalOpen] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState(
    '7987246c-9af5-489b-be85-d40a54e1681e',
  );

  // Fall back to the first available project if the default selection isn't
  // one this user actually has access to.
  useEffect(() => {
    if (projects.length === 0) return;
    if (!projects.some((p) => p.id === selectedProjectId)) {
      setSelectedProjectId(projects[0].id);
    }
  }, [projects, selectedProjectId]);

  // Fallback tab if landing on hidden/unsupported tab or when shared count is zero
  useEffect(() => {
    if (workspacesData) {
      if (workspaceUuid && isForeignWorkspace) {
        const hasMineProjects = counts.myProjects > 0 && counts.all > 1;
        const tabAllowed =
          tab === 'all' || (tab === 'mine' && hasMineProjects) || tab === 'archived';
        if (!tabAllowed) {
          setTab('all');
        }
      } else if (tab === 'shared' && !showSharedTab) {
        setTab('all');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    workspacesData,
    workspaceUuid,
    isForeignWorkspace,
    showSharedTab,
    tab,
    counts.myProjects,
    counts.all,
  ]);

  const selectedWorkspaceUuid = useMemo(
    () => projects.find((p) => p.id === selectedProjectId)?.workspaceUuid,
    [projects, selectedProjectId],
  );

  // New Project Form State
  const [newTitle, setNewTitle] = useState('');
  const [newType, setNewType] = useState<
    'Event' | 'Tour' | 'Music Video' | 'Album Rollout' | 'Festival' | 'Custom'
  >('Event');
  const [newStatus, setNewStatus] = useState<'Active' | 'Planning' | 'Completed'>('Planning');
  const [newStartDate, setNewStartDate] = useState('');
  const [newEndDate, setNewEndDate] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [newCustomProjectType, setNewCustomProjectType] = useState('');
  const [newImageFile, setNewImageFile] = useState<File | null>(null);
  const [newImagePreview, setNewImagePreview] = useState<string | null>(null);
  const [newImageError, setNewImageError] = useState<string | undefined>();
  const [isUploadingNewImage, setIsUploadingNewImage] = useState(false);
  const newImageInputRef = useRef<HTMLInputElement>(null);

  // Cover crop states
  const [isCropModalOpen, setIsCropModalOpen] = useState(false);
  const [cropSourceFile, setCropSourceFile] = useState<File | null>(null);
  const [cropSourceUrl, setCropSourceUrl] = useState<string | null>(null);
  const [isApplyingCrop, setIsApplyingCrop] = useState(false);

  const [formErrors, setFormErrors] = useState<{
    name?: string;
    type?: string;
    customProjectType?: string;
  }>({});

  useEffect(() => {
    return () => {
      if (newImagePreview) URL.revokeObjectURL(newImagePreview);
      if (cropSourceUrl) URL.revokeObjectURL(cropSourceUrl);
    };
  }, [newImagePreview, cropSourceUrl]);

  const handleNewImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setNewImageError('Please select a valid image file.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setNewImageError('Image file size must be 5 MB or smaller.');
      return;
    }
    setNewImageError(undefined);

    try {
      const convertedFile = await convertHeicToJpeg(file);
      if (cropSourceUrl) URL.revokeObjectURL(cropSourceUrl);
      const url = URL.createObjectURL(convertedFile);
      setCropSourceFile(convertedFile);
      setCropSourceUrl(url);
      setIsCropModalOpen(true);
    } catch (err) {
      console.error('Failed to prepare image for crop:', err);
      setNewImageError('Failed to process image. Please try another file.');
    }
  };

  const handleOpenRecrop = () => {
    if (cropSourceUrl) {
      setIsCropModalOpen(true);
    } else if (newImageInputRef.current) {
      newImageInputRef.current.value = '';
      newImageInputRef.current.click();
    }
  };

  const applyCoverCrop = async (cropAreaPixels: PixelCropArea) => {
    if (!cropSourceFile || !cropSourceUrl) return;
    setIsApplyingCrop(true);
    try {
      const croppedFile = await createCroppedImageFile({
        imageSrc: cropSourceUrl,
        cropAreaPixels,
        fileName: cropSourceFile.name,
      });

      if (newImagePreview) URL.revokeObjectURL(newImagePreview);
      const nextPreviewUrl = URL.createObjectURL(croppedFile);
      setNewImageFile(croppedFile);
      setNewImagePreview(nextPreviewUrl);
      setIsCropModalOpen(false);
    } catch (err) {
      console.error('Failed to crop image:', err);
      setNewImageError('Failed to crop image. Please try again.');
    } finally {
      setIsApplyingCrop(false);
    }
  };

  const handleRemoveNewImage = () => {
    setNewImageFile(null);
    setNewImagePreview(null);
    if (cropSourceUrl) URL.revokeObjectURL(cropSourceUrl);
    setCropSourceFile(null);
    setCropSourceUrl(null);
    setNewImageError(undefined);
    if (newImageInputRef.current) newImageInputRef.current.value = '';
  };

  // Filter projects according to permissions, guest visibility, and editor access.
  const filteredProjects = useMemo(() => {
    return projects.filter((p) => {
      // Guests cannot view projects they are invited to / can_view is false
      if (p.permissions && !p.permissions.can_view) return false;
      return true;
    });
  }, [projects]);

  const handleCreateProject = async (e: React.FormEvent) => {
    e.preventDefault();

    const trimmedTitle = newTitle.trim();
    const trimmedCustomType = newCustomProjectType.trim();
    const errors: { name?: string; type?: string; customProjectType?: string } = {};
    if (!trimmedTitle) {
      errors.name = 'Project name is required.';
    }
    // else if (projects.some((p) => p.title.trim().toLowerCase() === trimmedTitle.toLowerCase())) {
    //   errors.name = 'A project with this name already exists.';
    // }
    if (!newType) {
      errors.type = 'Select a project type.';
    }
    if (newType === 'Custom' && !trimmedCustomType) {
      errors.customProjectType = 'Custom Project Type is required.';
    }
    setFormErrors(errors);
    if (errors.name || errors.type || errors.customProjectType) return;

    let uploadedImageUrl: string | undefined;
    if (newImageFile) {
      setIsUploadingNewImage(true);
      try {
        const results = await uploadFilesToCloudinary([newImageFile], {
          folder: 'project_images',
          uploadPreset: 'service_preset',
        });
        uploadedImageUrl = results[0]?.secure_url;
      } catch (err) {
        console.error('Cloudinary upload error:', err);
        toast.error('Failed to upload image. Please try again.');
        setIsUploadingNewImage(false);
        return;
      }
      setIsUploadingNewImage(false);
    }

    createProject(
      {
        workspace_uuid: workspaceUuid,
        name: trimmedTitle,
        project_type: PROJECT_TYPE_LABEL_TO_API[newType],
        custom_project_type: newType === 'Custom' ? trimmedCustomType : '',
        status: PROJECT_STATUS_LABEL_TO_API[newStatus],
        start_date: newStartDate || null,
        end_date: newEndDate || null,
        description: newDescription || `${newType} project: ${trimmedTitle}`,
        ...(uploadedImageUrl ? { image: uploadedImageUrl } : {}),
      },
      {
        onSuccess: () => {
          setIsModalOpen(false);

          // Reset Form
          setNewTitle('');
          setNewType('Event');
          setNewStatus('Planning');
          setNewStartDate('');
          setNewEndDate('');
          setNewDescription('');
          setNewCustomProjectType('');
          setNewImageFile(null);
          setNewImagePreview(null);
          if (cropSourceUrl) URL.revokeObjectURL(cropSourceUrl);
          setCropSourceFile(null);
          setCropSourceUrl(null);
          setNewImageError(undefined);
          setFormErrors({});
        },
      },
    );
  };

  // Get project subtitle icon
  const getSubtypeIcon = (subtitle: string) => {
    switch (subtitle) {
      case 'Tour':
        return <FiMap className="w-3.5 h-3.5" />;
      case 'Music Video':
        return <FiTv className="w-3.5 h-3.5" />;
      case 'Album Rollout':
        return <FiMusic className="w-3.5 h-3.5" />;
      case 'Event':
      default:
        return <FiCalendar className="w-3.5 h-3.5" />;
    }
  };

  if (isLoading) {
    return <LoadingLogo />;
  }

  return (
    <div className="min-h-screen bg-black relative pb-32">
      <Header />

      <PageLayout className="pt-[110px]">
        {/* Title & Action Buttons Section */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            {workspaceUuid && isEditingWorkspaceName ? (
              <div className="flex items-center gap-2">
                <input
                  autoFocus
                  value={workspaceNameDraft}
                  onChange={(e) => setWorkspaceNameDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') saveWorkspaceName();
                    if (e.key === 'Escape') setIsEditingWorkspaceName(false);
                  }}
                  disabled={isSavingWorkspaceName}
                  className="bg-transparent border-b border-blue-500/40 outline-none text-3xl sm:text-4xl font-bold text-white tracking-tight disabled:opacity-50 min-w-0"
                />
                <button
                  onClick={saveWorkspaceName}
                  disabled={isSavingWorkspaceName}
                  className="text-blue-400 hover:text-blue-300 cursor-pointer disabled:opacity-50 shrink-0"
                  title="Save name"
                >
                  <FiCheck className="w-5 h-5" />
                </button>
                <button
                  onClick={() => setIsEditingWorkspaceName(false)}
                  disabled={isSavingWorkspaceName}
                  className="text-zinc-400 hover:text-white cursor-pointer disabled:opacity-50 shrink-0"
                  title="Cancel"
                >
                  <FiX className="w-5 h-5" />
                </button>
              </div>
            ) : (
              <h1 className="flex items-center gap-2.5 text-3xl sm:text-4xl font-bold text-white tracking-tight">
                <span>
                  {workspaceUuid ? (activeWorkspace?.name ?? 'Workspace') : 'Projects Workspace'}
                </span>
                {workspaceUuid && isWorkspaceOwner && (
                  <button
                    onClick={startEditingWorkspaceName}
                    className="text-zinc-500 hover:text-blue-400 cursor-pointer transition-colors"
                    title="Rename workspace"
                  >
                    <FiEdit2 className="w-5 h-5" />
                  </button>
                )}
              </h1>
            )}
            <p className="text-zinc-500 text-sm mt-1">
              Manage timelines, budgets, crew and operations
            </p>
            {workspaceUuid && (
              <button
                onClick={clearWorkspaceScope}
                className="inline-flex items-center gap-1.5 mt-3 px-3 py-1 rounded-full border border-blue-500/20 bg-blue-500/10 text-blue-400 text-[11px] font-semibold cursor-pointer hover:bg-blue-500/15 transition-all"
                title="Clear workspace filter"
              >
                <FiFolder className="w-3 h-3" />
                <span>{activeWorkspace?.name ?? 'Workspace'}</span>
                <FiX className="w-3 h-3" />
              </button>
            )}
          </div>

          <div className="flex items-center gap-3">
            {(!workspaceUuid || isWorkspaceOwner) && (
              <button
                onClick={() => setIsAccessModalOpen(true)}
                className="flex items-center gap-2 px-5 py-2.5 rounded-full border border-white/10 bg-zinc-900/60 text-zinc-300 text-xs font-semibold hover:bg-white/5 transition-all duration-200 cursor-pointer shadow-lg shadow-black/40"
              >
                <FiLock className="w-3.5 h-3.5" />
                <span>Manage Access</span>
                <FiArrowUpRight className="w-3.5 h-3.5 text-zinc-500" />
              </button>
            )}
            <button
              onClick={() => setIsModalOpen(true)}
              className="flex items-center gap-2 px-6 py-2.5 rounded-full bg-white text-black text-xs font-bold hover:bg-zinc-100 transition-all duration-200 cursor-pointer shadow-lg shadow-white/5 active:scale-95"
            >
              <FiPlus className="w-4 h-4 text-black stroke-[3]" />
              <span>New Project</span>
            </button>
          </div>
        </div>
        {/* Tab Filters */}
        <div className="flex items-center gap-2 border-b border-white/5 pb-4 mb-8 overflow-x-auto no-scrollbar">
          <button
            onClick={() => setTab('all')}
            className={`flex items-center gap-2.5 px-4 py-2 text-sm font-semibold rounded-full transition-all duration-200 cursor-pointer whitespace-nowrap shrink-0 ${
              tab === 'all'
                ? 'text-white bg-white/10'
                : 'text-zinc-400 hover:text-zinc-200 hover:bg-white/5'
            }`}
          >
            <FiFolder className="w-4 h-4" />
            <span>All</span>
            <span
              className={`text-[10px] px-2 py-0.5 rounded-full font-bold transition-all duration-200 ${
                tab === 'all' ? 'bg-white text-black' : 'bg-zinc-800 text-zinc-400'
              }`}
            >
              {counts.all}
            </span>
          </button>

          {(!isForeignWorkspace || (counts.myProjects > 0 && counts.all > 1)) && (
            <button
              onClick={() => setTab('mine')}
              className={`flex items-center gap-2.5 px-4 py-2 text-sm font-semibold rounded-full transition-all duration-200 cursor-pointer whitespace-nowrap shrink-0 ${
                tab === 'mine'
                  ? 'text-white bg-white/10'
                  : 'text-zinc-400 hover:text-zinc-200 hover:bg-white/5'
              }`}
            >
              <FiFolder className="w-4 h-4" />
              <span>My Projects</span>
              <span
                className={`text-[10px] px-2 py-0.5 rounded-full font-bold transition-all duration-200 ${
                  tab === 'mine' ? 'bg-white text-black' : 'bg-zinc-800 text-zinc-400'
                }`}
              >
                {counts.myProjects}
              </span>
            </button>
          )}

          {!isForeignWorkspace && showSharedTab && (
            <button
              onClick={() => setTab('shared')}
              className={`flex items-center gap-2.5 px-4 py-2 text-sm font-semibold rounded-full transition-all duration-200 cursor-pointer whitespace-nowrap shrink-0 ${
                tab === 'shared'
                  ? 'text-white bg-white/10'
                  : 'text-zinc-400 hover:text-zinc-200 hover:bg-white/5'
              }`}
            >
              <FiFolder className="w-4 h-4" />
              <span>Shared with me</span>
              <span
                className={`text-[10px] px-2 py-0.5 rounded-full font-bold transition-all duration-200 ${
                  tab === 'shared' ? 'bg-white text-black' : 'bg-zinc-800 text-zinc-400'
                }`}
              >
                {counts.sharedWithMe}
              </span>
            </button>
          )}

          <button
            onClick={() => setTab('archived')}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-full transition-all duration-200 cursor-pointer whitespace-nowrap shrink-0 ${
              tab === 'archived'
                ? 'text-white bg-white/10'
                : 'text-zinc-400 hover:text-zinc-200 hover:bg-white/5'
            }`}
          >
            <FiFolder className="w-4 h-4" />
            <span>Archived</span>
          </button>
        </div>

        {/* Project Cards Grid */}
        {filteredProjects.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 border border-white/5 bg-zinc-950/20 rounded-3xl text-center">
            <FiFolder className="w-12 h-12 text-zinc-600 mb-4" />
            <p className="text-zinc-400 font-semibold">
              {workspaceUuid
                ? `No projects in ${activeWorkspace?.name ?? 'this workspace'} yet`
                : 'No projects found'}
            </p>
            <p className="text-zinc-600 text-xs mt-1">
              {workspaceUuid
                ? 'Create a new project to get this workspace started.'
                : 'Try changing your filters or create a new project.'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredProjects.map((project) => {
              const spentPercent =
                project.allocatedBudget > 0
                  ? Math.round((project.spentBudget / project.allocatedBudget) * 100)
                  : 0;
              const isOverBudget = project.spentBudget > project.allocatedBudget;
              const canSeeBudget = project.isMyProject || project.showBudgetToCollaborators;

              // Color mappings based on status
              const statusColors = {
                Active: {
                  text: 'text-emerald-400',
                  bg: 'bg-emerald-500/10',
                  dot: 'bg-emerald-400',
                  progress: '#2563EB',
                },
                Planning: {
                  text: 'text-amber-400',
                  bg: 'bg-amber-500/10',
                  dot: 'bg-amber-400',
                  progress: '#EAB308',
                },
                Completed: {
                  text: 'text-blue-400',
                  bg: 'bg-blue-500/10',
                  dot: 'bg-blue-400',
                  progress: '#10B981',
                },
              };

              const colors = statusColors[project.status];

              return (
                <Link
                  key={project.id}
                  to="/projects/$projectId"
                  params={{ projectId: project.id }}
                  className="group block w-full aspect-square bg-[#0B0B0B] rounded-[17.28px] border-[0.72px] border-white/5 hover:border-white/10 transition-all duration-300 shadow-[0_8px_25px_rgba(0,0,0,0.5)] hover:scale-[1.01] overflow-hidden flex flex-col"
                >
                  {/* Card Banner Image */}
                  <div className="h-[38%] relative bg-zinc-900 overflow-hidden shrink-0">
                    <img
                      src={project.bgImage}
                      alt={project.title}
                      className="w-full h-full object-cover group-hover:scale-103 transition-transform duration-700 opacity-65"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0B0B0B] via-[#0B0B0B]/30 to-transparent" />

                    {/* Top overlay badges */}
                    <div className="absolute top-3 left-3 flex items-center gap-1.5 z-10">
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider ${
                          project.status === 'Active'
                            ? 'bg-[#0D2418]/85 text-emerald-400'
                            : project.status === 'Planning'
                              ? 'bg-[#241A0B]/85 text-amber-500'
                              : 'bg-[#0C1B2A]/85 text-blue-400'
                        } border border-white/5 backdrop-blur-md`}
                      >
                        <span
                          className={`w-1 h-1 rounded-full ${
                            project.status === 'Active'
                              ? 'bg-emerald-400'
                              : project.status === 'Planning'
                                ? 'bg-amber-400'
                                : 'bg-blue-400'
                          }`}
                        />
                        {project.status}
                      </span>
                      <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[9px] font-semibold text-zinc-300 bg-white/[0.06] border border-white/5 backdrop-blur-md">
                        {getSubtypeIcon(project.subtitle)}
                        <span>{project.subtitle}</span>
                      </span>
                    </div>
                  </div>

                  {/* Card Content */}
                  <div className="px-4 pb-4 pt-3 flex flex-col justify-between h-[62%] flex-grow">
                    <div>
                      {/* Title & Dates */}
                      <h2 className="text-white text-[15px] font-bold leading-tight line-clamp-1 group-hover:text-zinc-200 transition-colors">
                        {project.title}
                      </h2>
                      <p className="text-zinc-550 text-[10px] mt-1.5 flex items-center gap-1 font-medium">
                        <FiCalendar className="w-3 h-3 text-zinc-550 shrink-0" />
                        <span>
                          {project.startDate} — {project.endDate}
                        </span>
                      </p>
                    </div>

                    <div className="space-y-3.5">
                      {/* Progress Bar */}
                      <div>
                        <div className="flex items-center justify-between text-[10px] font-semibold mb-1">
                          <span className="text-zinc-400">Progress</span>
                          <span
                            style={{
                              color: colors.progress === '#2563EB' ? '#3B82F6' : colors.progress,
                            }}
                          >
                            {project.progress}%
                          </span>
                        </div>
                        <div className="w-full h-[3px] bg-zinc-900 rounded-full overflow-hidden">
                          <div
                            className="h-full rounded-full transition-all duration-500"
                            style={{
                              width: `${project.progress}%`,
                              backgroundColor:
                                colors.progress === '#2563EB' ? '#3B82F6' : colors.progress,
                            }}
                          />
                        </div>
                      </div>

                      {/* Core Metric Grid */}
                      <div className="grid grid-cols-3 gap-2 pb-3 pt-0.5 text-left border-b border-white/5">
                        <div className="flex flex-col items-start">
                          <div className="flex items-center gap-1 text-zinc-500 text-[10px] font-semibold">
                            <FiUsers className="w-3 h-3 shrink-0" />
                            <span>Crew</span>
                          </div>
                          <p className="text-white font-bold text-[13px] mt-0.5">
                            {project.crewCount}
                          </p>
                        </div>
                        <div className="flex flex-col items-start">
                          <div className="flex items-center gap-1 text-zinc-500 text-[10px] font-semibold">
                            <FiCheckSquare className="w-3 h-3 shrink-0" />
                            <span>Tasks</span>
                          </div>
                          {project.status === 'Completed' ? (
                            <p className="text-emerald-400 font-bold text-[13px] mt-0.5">Done</p>
                          ) : (
                            <p className="text-white font-bold text-[13px] mt-0.5">
                              {project.taskCount}
                            </p>
                          )}
                        </div>
                        <div className="flex flex-col items-start">
                          <div className="flex items-center gap-1 text-zinc-500 text-[10px] font-semibold">
                            <FiDollarSign className="w-3 h-3 shrink-0" />
                            <span>Budget</span>
                          </div>
                          {canSeeBudget ? (
                            <p
                              className={`font-bold text-[13px] mt-0.5 ${isOverBudget ? 'text-rose-500' : 'text-white'}`}
                            >
                              {spentPercent}%
                            </p>
                          ) : (
                            <p className="flex items-center gap-1 text-zinc-600 font-bold text-[13px] mt-0.5">
                              <FiLock className="w-3 h-3" />
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Spent vs Budget Allocated */}
                      {canSeeBudget && (
                        <div>
                          <div className="flex items-center justify-between text-[9px] font-medium text-zinc-550">
                            <span>${project.spentBudget.toLocaleString()} spent</span>
                            <span>${project.allocatedBudget.toLocaleString()} allocated</span>
                          </div>
                          <div className="w-full h-[2.5px] bg-zinc-900 rounded-full mt-1.5 overflow-hidden">
                            <div
                              className={`h-full rounded-full ${isOverBudget ? 'bg-rose-500' : 'bg-emerald-500'}`}
                              style={{ width: `${Math.min(spentPercent, 100)}%` }}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </PageLayout>

      <BottomNav />

      {/* Create Project Modal */}
      <AnimatePresence>
        {isModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsModalOpen(false)}
              className="absolute inset-0 bg-black/80 backdrop-blur-md"
            />

            {/* Modal Body */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ type: 'spring', duration: 0.4 }}
              className="relative w-full max-w-lg bg-[#0B0B0B] border border-white/10 rounded-[28px] p-6 text-left flex flex-col z-10 shadow-[0_20px_50px_rgba(0,0,0,0.8)]"
            >
              {/* Header */}
              <div className="flex items-start justify-between mb-6">
                <div>
                  <h2 className="text-xl font-bold text-white">Create Project</h2>
                  <p className="text-zinc-500 text-xs mt-1">
                    Bring your production together in one workspace.
                  </p>
                </div>
                <button
                  onClick={() => setIsModalOpen(false)}
                  className="p-1.5 rounded-full border border-white/10 hover:border-white/20 bg-zinc-900/60 hover:bg-zinc-800 text-zinc-400 hover:text-white transition-all cursor-pointer"
                >
                  <FiX className="w-4 h-4" />
                </button>
              </div>

              {/* Form */}
              <form onSubmit={handleCreateProject} className="space-y-4">
                {/* Project Image */}
                <div>
                  <label className="block text-zinc-400 text-xs font-semibold mb-2">
                    Project Image <span className="text-zinc-600 font-normal">(optional)</span>
                  </label>
                  <input
                    ref={newImageInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleNewImageChange}
                  />
                  {newImagePreview ? (
                    <div className="relative w-full aspect-[16/9] rounded-2xl overflow-hidden border border-white/10 bg-[#060606] group shadow-lg">
                      <img
                        src={newImagePreview}
                        alt="Project Cover Preview"
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-black/30 opacity-95 group-hover:opacity-100 transition-opacity flex flex-col justify-between p-3">
                        <div className="flex items-center justify-end">
                          <button
                            type="button"
                            onClick={handleRemoveNewImage}
                            title="Remove image"
                            className="p-1.5 rounded-full bg-black/60 hover:bg-rose-500/80 text-white transition-all cursor-pointer shadow-md"
                          >
                            <FiX className="w-3.5 h-3.5" />
                          </button>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => newImageInputRef.current?.click()}
                            className="px-3 py-1.5 rounded-full bg-black/70 hover:bg-black/90 backdrop-blur-md text-white text-xs font-semibold transition-all cursor-pointer border border-white/15 flex items-center gap-1.5 shadow-md active:scale-95"
                          >
                            <FiUpload className="w-3.5 h-3.5 text-blue-400" />
                            <span>Change</span>
                          </button>
                          {cropSourceUrl && (
                            <button
                              type="button"
                              onClick={handleOpenRecrop}
                              className="px-3 py-1.5 rounded-full bg-black/70 hover:bg-black/90 backdrop-blur-md text-white text-xs font-semibold transition-all cursor-pointer border border-white/15 flex items-center gap-1.5 shadow-md active:scale-95"
                            >
                              <FiCrop className="w-3.5 h-3.5 text-emerald-400" />
                              <span>Adjust Crop</span>
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => newImageInputRef.current?.click()}
                      className="w-32 aspect-square border border-dashed rounded-2xl flex flex-col items-center justify-center gap-1.5 text-center cursor-pointer transition-all p-2 border-white/10 hover:border-white/20 bg-[#0F0F0F]/60 hover:bg-white/1"
                    >
                      <FiUpload className="w-5 h-5 text-zinc-500" />
                      <p className="text-[10px] text-zinc-300 font-semibold leading-tight">
                        Upload — <span className="text-blue-500">browse</span>
                      </p>
                      <p className="text-[9px] text-zinc-550">Up to 5 MB</p>
                    </button>
                  )}
                  {newImageError && (
                    <p className="text-rose-500 text-[10px] font-semibold mt-1.5">
                      {newImageError}
                    </p>
                  )}
                </div>

                {/* Project Name */}
                <div>
                  <label className="block text-zinc-400 text-xs font-semibold mb-2">
                    Project Name <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. Summer Sound Clash 2026"
                    value={newTitle}
                    onChange={(e) => {
                      setNewTitle(e.target.value);
                      if (formErrors.name) setFormErrors((prev) => ({ ...prev, name: undefined }));
                    }}
                    className={`w-full bg-[#0F0F0F] border hover:border-white/10 focus:border-blue-500 outline-none text-white text-xs rounded-xl px-4 py-3 transition-all ${
                      formErrors.name ? 'border-rose-500' : 'border-white/5'
                    }`}
                  />
                  {formErrors.name && (
                    <p className="text-rose-500 text-[10px] font-semibold mt-1.5">
                      {formErrors.name}
                    </p>
                  )}
                </div>

                {/* Project Type */}
                <div>
                  <label className="block text-zinc-400 text-xs font-semibold mb-2">
                    Project Type <span className="text-rose-500">*</span>
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {[
                      { id: 'Event', icon: <FiCalendar className="w-3.5 h-3.5" /> },
                      { id: 'Tour', icon: <FiMap className="w-3.5 h-3.5" /> },
                      { id: 'Music Video', icon: <FiTv className="w-3.5 h-3.5" /> },
                      { id: 'Album Rollout', icon: <FiMusic className="w-3.5 h-3.5" /> },
                      { id: 'Festival', icon: <FiUsers className="w-3.5 h-3.5" /> },
                      { id: 'Custom', icon: <FiFolder className="w-3.5 h-3.5" /> },
                    ].map((typeItem) => (
                      <button
                        key={typeItem.id}
                        type="button"
                        onClick={() => {
                          setNewType(typeItem.id as typeof newType);
                          if (formErrors.type || formErrors.customProjectType)
                            setFormErrors((prev) => ({
                              ...prev,
                              type: undefined,
                              customProjectType: undefined,
                            }));
                          if (typeItem.id !== 'Custom') setNewCustomProjectType('');
                        }}
                        className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-xs font-semibold border transition-all cursor-pointer ${
                          newType === typeItem.id
                            ? 'bg-blue-600 border-blue-600 text-white shadow-md shadow-blue-500/20'
                            : 'bg-zinc-900/60 border-white/5 text-zinc-400 hover:text-white hover:border-white/10'
                        }`}
                      >
                        {typeItem.icon}
                        <span>{typeItem.id}</span>
                      </button>
                    ))}
                  </div>
                  {formErrors.type && (
                    <p className="text-rose-500 text-[10px] font-semibold mt-1.5">
                      {formErrors.type}
                    </p>
                  )}
                  <AnimatePresence>
                    {newType === 'Custom' && (
                      <motion.div
                        initial={{ opacity: 0, height: 0, marginTop: 0 }}
                        animate={{ opacity: 1, height: 'auto', marginTop: 10 }}
                        exit={{ opacity: 0, height: 0, marginTop: 0 }}
                        transition={{ duration: 0.2 }}
                        className="overflow-hidden"
                      >
                        <label className="block text-zinc-400 text-xs font-semibold mb-2">
                          Custom Project Type <span className="text-rose-500">*</span>
                        </label>
                        <input
                          type="text"
                          placeholder="e.g. Marketing Campaign"
                          value={newCustomProjectType}
                          onChange={(e) => {
                            setNewCustomProjectType(e.target.value);
                            if (formErrors.customProjectType)
                              setFormErrors((prev) => ({ ...prev, customProjectType: undefined }));
                          }}
                          className={`w-full bg-[#0F0F0F] border hover:border-white/10 focus:border-blue-500 outline-none text-white text-xs rounded-xl px-4 py-3 transition-all ${
                            formErrors.customProjectType ? 'border-rose-500' : 'border-white/5'
                          }`}
                        />
                        {formErrors.customProjectType && (
                          <p className="text-rose-500 text-[10px] font-semibold mt-1.5">
                            {formErrors.customProjectType}
                          </p>
                        )}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* Start & End Dates */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-zinc-400 text-xs font-semibold mb-2">
                      Start Date
                    </label>
                    <div className="relative">
                      <FiCalendar className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-550 w-4 h-4 pointer-events-none" />
                      <input
                        type="date"
                        value={newStartDate}
                        onChange={(e) => setNewStartDate(e.target.value)}
                        className="w-full bg-[#0F0F0F] border border-white/5 hover:border-white/10 focus:border-blue-500 outline-none text-white text-xs rounded-xl pl-11 pr-4 py-3 transition-all cursor-pointer scheme-dark"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-zinc-400 text-xs font-semibold mb-2">
                      End Date
                    </label>
                    <div className="relative">
                      <FiCalendar className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-550 w-4 h-4 pointer-events-none" />
                      <input
                        type="date"
                        value={newEndDate}
                        onChange={(e) => setNewEndDate(e.target.value)}
                        className="w-full bg-[#0F0F0F] border border-white/5 hover:border-white/10 focus:border-blue-500 outline-none text-white text-xs rounded-xl pl-11 pr-4 py-3 transition-all cursor-pointer scheme-dark"
                      />
                    </div>
                  </div>
                </div>

                {/* Description */}
                <div>
                  <label className="block text-zinc-400 text-xs font-semibold mb-2">
                    Description
                  </label>
                  <textarea
                    placeholder="What's this project about?"
                    value={newDescription}
                    onChange={(e) => setNewDescription(e.target.value)}
                    rows={4}
                    className="w-full bg-[#0F0F0F] border border-white/5 hover:border-white/10 focus:border-blue-500 outline-none text-white text-xs rounded-xl px-4 py-3 resize-none transition-all"
                  />
                </div>

                {/* Create Project Button */}
                <div className="flex items-center justify-end pt-4 border-t border-white/5">
                  <button
                    type="submit"
                    disabled={isUploadingNewImage}
                    className="px-6 py-2.5 rounded-full bg-white text-black hover:bg-zinc-100 text-xs font-bold transition-all cursor-pointer shadow-lg shadow-white/5 active:scale-95 hover:scale-[1.02] disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:scale-100"
                  >
                    {isUploadingNewImage ? 'Uploading...' : 'Create Project'}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Collaborators & Access Modal */}
      <AnimatePresence>
        {isAccessModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsAccessModalOpen(false)}
              className="absolute inset-0 bg-black/80 backdrop-blur-md"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="relative w-full max-w-lg bg-[#0B0B0B] border border-white/10 rounded-[28px] p-6 text-left flex flex-col max-h-[85vh] z-10 shadow-[0_20px_50px_rgba(0,0,0,0.8)]"
            >
              {/* Header */}
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h2 className="text-xl font-bold text-white">Collaborators & Access</h2>
                  <p className="text-zinc-500 text-xs mt-1">
                    Manage who has access to this project and their role.
                  </p>
                </div>
                <button
                  onClick={() => setIsAccessModalOpen(false)}
                  className="p-1.5 rounded-full border border-white/10 hover:border-white/20 bg-zinc-900/60 hover:bg-zinc-800 text-zinc-400 hover:text-white transition-all cursor-pointer"
                >
                  <FiX className="w-4 h-4" />
                </button>
              </div>

              {/* Project Select */}
              <div className="pt-2 border-t border-white/5">
                <label className="block text-zinc-400 text-xs font-semibold mb-2">Project</label>
                <select
                  value={selectedProjectId}
                  onChange={(e) => setSelectedProjectId(e.target.value)}
                  className="w-full bg-zinc-900 border border-white/5 hover:border-white/10 focus:border-blue-500 outline-none text-white text-xs rounded-xl px-3.5 py-3 transition-all cursor-pointer"
                >
                  {projects.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.title}
                    </option>
                  ))}
                </select>
              </div>

              {/* Live workspace-backed collaborators & access */}
              <div className="flex-1 overflow-y-auto mt-4 pr-1 min-h-[220px] scrollbar-thin">
                <CollaboratorsAccessCard
                  workspaceUuid={selectedWorkspaceUuid}
                  currentUserId={user?.id}
                  embedded
                />
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
      <CoverCropModal
        isOpen={isCropModalOpen}
        imageSrc={cropSourceUrl}
        isSubmitting={isApplyingCrop}
        aspect={16 / 9}
        objectFit="contain"
        title="Adjust project cover crop"
        description="Drag and scale to choose what appears in your project banner cover"
        onCancel={() => setIsCropModalOpen(false)}
        onConfirm={applyCoverCrop}
      />

      {workspaceModalOpen && <WorkspaceModal onClose={() => setWorkspaceModalOpen(false)} />}
    </div>
  );
}
